import { classes } from "../react"
import { InputWithPopup } from "./InputWithPopup"
import { QueryResults } from "./QueryResults"
import { MaxHeight } from "./types"

interface Props<T> {

    source:
    | { items: T[] }
    | { queryAsync(query: string): PromiseLike<T[]>, debounce: number }

    maxHeight?: MaxHeight
    align?: "left" | "right"

    placeholder?: string

    selected: T | undefined
    setSelected(item: T): void

    text: string
    setText(text: string): void

    textSelector(item: T): string
    keySelector(item: T): string | number

    applyFilter?: boolean

    openOnFocus?: boolean
    openOnMouseDown?: boolean

    header?: React.ReactNode
    noMatch?: React.ReactNode
    matchContent?(item: T): React.ReactNode

    inputStyle?: React.CSSProperties
    inputCssClass?: string

    readOnly?: boolean
    disabled?: boolean

}

export function Typeahead2<T>(props: Props<T>) {

    const { text, setText, selected, textSelector, readOnly, disabled } = props

    const resetText = (item = selected) => setText(item ? textSelector(item) : "")

    const applyFilter = props.applyFilter ?? (
        (!selected) || (textSelector(selected) != text)
    )

    React.useEffect(resetText, [selected])

    const setSelected = (item: T) => {
        resetText(item)
        props.setSelected(item)
    }

    const handleLostFocus = () => {

        if (!text) {
            props.setSelected(undefined)
        }
        else if (selected && (textSelector(selected) !== text)) {
            resetText()
        }
        // 240218 - DevOps #1448
        // This clause has been added to make it impossible to input text that does not match the current selection
        // I think all current use cases should match this behaviour, but if we need to implement so kind of actual "combo box",
        // used either to select an item or input some free text, then we would  need to add a prop to switch this behaviour on
        else if ((!selected) && Boolean(text)) {
            resetText()
        }

    }


    return <InputWithPopup
        content={inputProps =>
            <List
                text={inputProps.text}
                close={inputProps.close}
                source={props.source}
                selected={props.selected}
                setSelected={setSelected}
                textSelector={props.textSelector}
                keySelector={props.keySelector}
                applyFilter={applyFilter}
                header={props.header}
                noMatch={props.noMatch}
                matchContent={props.matchContent}
            />
        }
        cssClass={classes("omnia-typeahead", props.inputCssClass)}
        inputStyle={props.inputStyle}
        placeholder={props.placeholder}
        align={props.align}
        maxHeight={props.maxHeight}
        readOnly={readOnly}
        disabled={disabled}
        text={text}
        setText={setText}
        openOnFocus={props.openOnFocus}
        openOnMouseDown={props.openOnMouseDown}
        onLostFocus={handleLostFocus}
    />


}


interface ListProps<T> {

    source: Props<T>["source"]

    textSelector(item: T): string
    keySelector(item: T): string | number

    text: string

    selected: T
    setSelected(item: T | undefined): void

    close(): void

    applyFilter: boolean

    header?: React.ReactNode
    noMatch?: React.ReactNode
    matchContent?(item: T): React.ReactNode

}

function List<T>(props: ListProps<T>) {

    const { source, textSelector } = props

    return <QueryResults<T>
        {...source}
        textSelector={textSelector}
        close={props.close}
        applyFilter={props.applyFilter}
        keySelector={props.keySelector}
        selected={props.selected}
        setSelected={props.setSelected}
        text={props.text}
        header={props.header}
        noMatch={props.noMatch}
        matchContent={props.matchContent}
    />


}
